import { useMemo, useState } from 'react'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import { Box, IconButton, ListSubheader, Menu, MenuItem, Typography } from '@mui/material'
import type { TEnumItem } from '@modules/types'
import { REQUESTS_LIST_COLUMNS_STRINGS } from './strings'

export const PUBLISHER_APPROVE_REQUEST_STATUS_ENUM = 'publisher_approve_request_status_enum'
export const PUBLISHER_COPYRIGHT_REQUEST_STATUS_ENUM = 'publisher_copyright_request_status_enum'

export type TPublisherRequestListStatusFilter = {
  enumName:
    | typeof PUBLISHER_APPROVE_REQUEST_STATUS_ENUM
    | typeof PUBLISHER_COPYRIGHT_REQUEST_STATUS_ENUM
  statusId: number
} | null

type StatusColumnHeaderProps = {
  enums: TEnumItem[]
  enumsLoading: boolean
  statusFilter: TPublisherRequestListStatusFilter
  onStatusFilterChange: (filter: TPublisherRequestListStatusFilter) => void
}

export const StatusColumnHeader = ({
  enums,
  enumsLoading,
  statusFilter,
  onStatusFilterChange,
}: StatusColumnHeaderProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const open = Boolean(anchorEl)

  const approvalItems = useMemo(
    () =>
      enums
        .filter(({ enumName }) => enumName === PUBLISHER_APPROVE_REQUEST_STATUS_ENUM)
        .sort((a, b) => a.id - b.id),
    [enums],
  )

  const copyrightItems = useMemo(
    () =>
      enums
        .filter(({ enumName }) => enumName === PUBLISHER_COPYRIGHT_REQUEST_STATUS_ENUM)
        .sort((a, b) => a.id - b.id),
    [enums],
  )

  const isFiltered = statusFilter !== null

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 0.5,
          width: '100%',
          pr: 0.5,
        }}
      >
        <Typography
          component="span"
          variant="body2"
          sx={{ fontWeight: 600, color: 'text.secondary' }}
        >
          {REQUESTS_LIST_COLUMNS_STRINGS.status}
        </Typography>
        <IconButton
          size="small"
          onClick={e => setAnchorEl(e.currentTarget)}
          disabled={enumsLoading}
          aria-label={REQUESTS_LIST_COLUMNS_STRINGS.statusFilterMenuAria}
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
          sx={{
            color: theme =>
              isFiltered ? theme.palette.primary.main : theme.palette.text.secondary,
          }}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
      </Box>
      <Menu anchorEl={anchorEl} open={open} onClose={() => setAnchorEl(null)}>
        <MenuItem
          onClick={() => {
            onStatusFilterChange(null)
            setAnchorEl(null)
          }}
          selected={!statusFilter}
        >
          {REQUESTS_LIST_COLUMNS_STRINGS.statusFilterAll}
        </MenuItem>
        <ListSubheader disableSticky sx={{ fontWeight: 600, lineHeight: 2.5 }}>
          {REQUESTS_LIST_COLUMNS_STRINGS.statusFilterApprovalSection}
        </ListSubheader>
        {approvalItems.map(item => (
          <MenuItem
            key={`approve-${item.id}`}
            onClick={() => {
              onStatusFilterChange({
                enumName: PUBLISHER_APPROVE_REQUEST_STATUS_ENUM,
                statusId: item.id,
              })
              setAnchorEl(null)
            }}
            selected={
              statusFilter?.enumName === PUBLISHER_APPROVE_REQUEST_STATUS_ENUM &&
              statusFilter.statusId === item.id
            }
            sx={{ pl: 3 }}
          >
            {item.name}
          </MenuItem>
        ))}
        <ListSubheader disableSticky sx={{ fontWeight: 600, lineHeight: 2.5 }}>
          {REQUESTS_LIST_COLUMNS_STRINGS.statusFilterCopyrightSection}
        </ListSubheader>
        {copyrightItems.map(item => (
          <MenuItem
            key={`copyright-${item.id}`}
            onClick={() => {
              onStatusFilterChange({
                enumName: PUBLISHER_COPYRIGHT_REQUEST_STATUS_ENUM,
                statusId: item.id,
              })
              setAnchorEl(null)
            }}
            selected={
              statusFilter?.enumName === PUBLISHER_COPYRIGHT_REQUEST_STATUS_ENUM &&
              statusFilter.statusId === item.id
            }
            sx={{ pl: 3 }}
          >
            {item.name}
          </MenuItem>
        ))}
      </Menu>
    </>
  )
}

export const statusFilterToListFilter = (statusFilter: TPublisherRequestListStatusFilter) => {
  if (!statusFilter) return []
  const field =
    statusFilter.enumName === PUBLISHER_APPROVE_REQUEST_STATUS_ENUM
      ? 'approve_status'
      : 'copyright_status'
  return [{ field, operator: 'eq' as const, value: statusFilter.statusId }]
}
